import { Controller } from '@hotwired/stimulus'

// Desktop mega-menus open on hover and swap as the pointer moves along the trigger
// row, which Bootstrap's dropdown doesn't do on its own. Below `lg` the same menus
// are left alone so they stay click-driven accordions inside the hamburger sheet.
//
// Triggers are clicked rather than driven through Bootstrap's JS API: the marketing
// bundle has no `window.bootstrap`, and going through the data-api keeps us on the
// one dropdown instance it already owns (including its close-the-others handling).
export default class extends Controller {
    static targets = ['toggle', 'row']

    connect() {
        this.desktop = window.matchMedia('(min-width: 992px)')

        this.onScroll = () => {
            if (this.scrollFrame) return
            this.scrollFrame = requestAnimationFrame(() => {
                this.scrollFrame = null
                this.element.classList.toggle('rtm-navbar--scrolled', window.scrollY > 12)
            })
        }
        this.onResize = () => {
            const menu = this.openMenu()
            if (menu) this.positionMenu(menu)
            if (this.sheetOpen() && !this.desktop.matches) this.measureSheet()
            else this.unlockScroll()
        }

        window.addEventListener('scroll', this.onScroll, { passive: true })
        window.addEventListener('resize', this.onResize)
        this.onScroll()
    }

    disconnect() {
        window.removeEventListener('scroll', this.onScroll)
        window.removeEventListener('resize', this.onResize)
        cancelAnimationFrame(this.scrollFrame)
        cancelAnimationFrame(this.stateFrame)
        this.unlockScroll()
    }

    // The bar's height moves with the logo's line box and the row's padding, so the sheet's
    // top edge has to be measured. `show.bs.collapse` fires while the panel is still
    // zero-height, which is what leaves the bar at its closed size here.
    sheetShown() {
        if (this.desktop.matches) return

        this.measureSheet()
        document.body.classList.add('overflow-hidden')
    }

    sheetHidden() {
        this.unlockScroll()
    }

    unlockScroll() {
        document.body.classList.remove('overflow-hidden')
    }

    measureSheet() {
        if (!this.hasRowTarget) return

        const bottom = Math.max(0, Math.round(this.rowTarget.getBoundingClientRect().bottom))
        this.element.style.setProperty('--rtm-sheet-top', `${bottom}px`)
    }

    sheetOpen() {
        return Boolean(this.element.querySelector('.navbar-collapse.show'))
    }

    // A class rather than a CSS `:hover` so it can be held to desktop — touch either never
    // fires hover or leaves it stuck on after a tap.
    hoverStart() {
        if (!this.desktop.matches) return

        this.element.classList.add('rtm-navbar--hover')
    }

    hoverEnd() {
        this.element.classList.remove('rtm-navbar--hover')
    }

    open(event) {
        if (!this.desktop.matches) return

        this.clickWhenExpanded(event.currentTarget, false)
        // Bootstrap focuses the trigger it opens, which paints a focus ring on a menu the
        // pointer merely swept over. Keyboard opens go straight through the data-api and
        // never reach this handler, so dropping focus here only affects hover.
        event.currentTarget.blur()
    }

    closeAll() {
        if (!this.desktop.matches) return

        this.toggleTargets.forEach((toggle) => this.clickWhenExpanded(toggle, true))
    }

    menuShown(event) {
        const menu = event.target.parentElement.querySelector('.rtm-megamenu')
        if (menu) this.positionMenu(menu)

        this.syncOpenState()
    }

    menuHidden() {
        this.syncOpenState()
    }

    // Sliding from one trigger to the next closes the old panel and opens the new one, and
    // Bootstrap's `hidden` can land after the matching `shown`. Reading the open panel back
    // off the DOM a frame later collapses the swap into one state change, so the bar doesn't
    // flash its closed background halfway through.
    syncOpenState() {
        cancelAnimationFrame(this.stateFrame)
        this.stateFrame = requestAnimationFrame(() => {
            const open = this.desktop.matches && this.openMenu()
            this.element.classList.toggle('rtm-navbar--open', Boolean(open))
        })
    }

    // Panels are centred on their trigger but kept inside the container's gutters, so Call
    // Tracking's wide panel starts at the left gutter while the narrow Resources panel sits
    // under its own label.
    positionMenu(menu) {
        if (!this.desktop.matches || !this.hasRowTarget) return

        const trigger = menu.parentElement.querySelector('[data-marketing-navbar-target~="toggle"]')
        if (!trigger) return

        const row = this.rowTarget.getBoundingClientRect()
        const gutter = parseFloat(getComputedStyle(this.rowTarget).paddingLeft)
        const box = trigger.getBoundingClientRect()
        const centred = box.left - row.left + box.width / 2 - menu.offsetWidth / 2
        const rightmost = row.width - gutter - menu.offsetWidth

        menu.style.setProperty(
            '--rtm-megamenu-left',
            `${Math.round(Math.max(gutter, Math.min(centred, rightmost)))}px`,
        )
    }

    openMenu() {
        return this.element.querySelector('.rtm-megamenu.show')
    }

    clickWhenExpanded(element, expanded) {
        if ((element.getAttribute('aria-expanded') === 'true') === expanded) element.click()
    }
}
